/**
 * =============================================================================
 * SpriteFrameEditorScene - Editor visual para ajuste de gráficos de movimiento
 * =============================================================================
 * 
 * Editor para cargar sprite sheets y ajustar frames que no están centrados
 * o alineados correctamente.
 * 
 * Características:
 * - Cargar imagen desde archivo
 * - Configurar grid (filas x columnas)
 * - Visualizar rectángulos de frames
 * - Ajustar bordes de frames arrastrando
 * - Ajustar offset de centrado
 * - Exportar PNG ajustado
 */

import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';

/**
 * Ajuste de un frame individual
 */
export interface FrameAdjustment {
  frameIndex: number;
  cropX: number;      // Recorte izquierdo (relativo al frame original)
  cropY: number;      // Recorte superior (relativo al frame original)
  cropWidth: number;  // Ancho después de recorte
  cropHeight: number; // Alto después de recorte
  offsetX: number;    // Offset de centrado X
  offsetY: number;    // Offset de centrado Y
}

/**
 * Configuración del sprite sheet
 */
export interface SpriteSheetConfig {
  rows: number;
  columns: number;
  frameWidth: number;
  frameHeight: number;
  adjustments: Map<number, FrameAdjustment>;
}

/**
 * Tipo de handle del canvas (bordes y esquinas)
 */
type CanvasHandleType = 
  | 'left' | 'right' | 'top' | 'bottom'
  | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  | null;

/**
 * Colores para direcciones (filas del sprite sheet)
 */
const DIRECTION_COLORS: Record<number, number> = {
  0: 0x00ff00, // Down - Verde
  1: 0x0088ff, // Up - Azul
  2: 0xffff00, // Left - Amarillo
  3: 0xff4444, // Right - Rojo
};

/**
 * Información del crop del canvas
 */
export interface CanvasCropInfo {
  left: number;
  right: number;
  top: number;
  bottom: number;
  croppedWidth: number;
  croppedHeight: number;
}

/**
 * Callback types para comunicación con panel HTML
 */
export type OnImageLoadedCallback = (width: number, height: number, fileName: string) => void;
export type OnFrameSelectedCallback = (frameIndex: number | null, adjustment: FrameAdjustment | null) => void;
export type OnConfigChangedCallback = (config: SpriteSheetConfig) => void;
export type OnCanvasCropChangedCallback = (cropInfo: CanvasCropInfo) => void;

export class SpriteFrameEditorScene extends Phaser.Scene {
  // Callbacks estáticos para comunicación con el panel HTML
  public static onImageLoaded: OnImageLoadedCallback | null = null;
  public static onFrameSelected: OnFrameSelectedCallback | null = null;
  public static onConfigChanged: OnConfigChangedCallback | null = null;
  public static onCanvasCropChanged: OnCanvasCropChangedCallback | null = null;

  // Imagen cargada
  private loadedImageKey: string | null = null;
  private loadedImage: Phaser.GameObjects.Image | null = null;
  private imageWidth: number = 0;
  private imageHeight: number = 0;
  private fileName: string = '';

  // Configuración del grid
  private config: SpriteSheetConfig = {
    rows: 4,
    columns: 6,
    frameWidth: 0,
    frameHeight: 0,
    adjustments: new Map(),
  };

  // Estado del editor
  private selectedFrameIndex: number | null = null;
  private hoveredFrameIndex: number | null = null;
  private isDraggingCamera: boolean = false;
  private isDraggingHandle: boolean = false;
  private dragHandle: 'left' | 'right' | 'top' | 'bottom' | null = null;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private dragStartScrollX: number = 0;
  private dragStartScrollY: number = 0;

  // Estado para redimensionar canvas
  private isDraggingCanvasHandle: boolean = false;
  private canvasHandle: CanvasHandleType = null;
  private canvasCropLeft: number = 0;   // Píxeles recortados desde la izquierda
  private canvasCropRight: number = 0;  // Píxeles recortados desde la derecha
  private canvasCropTop: number = 0;    // Píxeles recortados desde arriba
  private canvasCropBottom: number = 0; // Píxeles recortados desde abajo

  // Gráficos
  private gridGraphics: Phaser.GameObjects.Graphics | null = null;
  private frameRectGraphics: Phaser.GameObjects.Graphics | null = null;
  private selectionGraphics: Phaser.GameObjects.Graphics | null = null;
  private handleGraphics: Phaser.GameObjects.Graphics | null = null;
  private showGrid: boolean = true;

  // Tooltip
  private tooltipContainer: Phaser.GameObjects.Container | null = null;

  // Controles de teclado
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;

  // Handles de selección (para detección de clicks)
  private handleRects: {
    left: Phaser.Geom.Rectangle;
    right: Phaser.Geom.Rectangle;
    top: Phaser.Geom.Rectangle;
    bottom: Phaser.Geom.Rectangle;
  } | null = null;

  // Handles del canvas (para redimensionar la imagen completa)
  private canvasHandleGraphics: Phaser.GameObjects.Graphics | null = null;
  private canvasHandleRects: Record<string, Phaser.Geom.Rectangle> | null = null;

  constructor() {
    super({ key: 'SpriteFrameEditorScene' });
  }

  preload(): void {
    console.log('[SpriteFrameEditorScene] preload()');
    // No precargamos nada, las imágenes se cargan dinámicamente
  }

  create(): void {
    console.log('[SpriteFrameEditorScene] create()');

    // Configurar cámara sin bounds para permitir pan libre
    const cam = this.cameras.main;
    cam.removeBounds();
    cam.setZoom(1);
    cam.centerOn(0, 0);

    // Crear layers de gráficos
    this.gridGraphics = this.add.graphics();
    this.gridGraphics.setDepth(1);

    this.frameRectGraphics = this.add.graphics();
    this.frameRectGraphics.setDepth(2);

    this.selectionGraphics = this.add.graphics();
    this.selectionGraphics.setDepth(3);

    this.handleGraphics = this.add.graphics();
    this.handleGraphics.setDepth(4);

    this.canvasHandleGraphics = this.add.graphics();
    this.canvasHandleGraphics.setDepth(5);

    // Configurar controles de teclado
    this.cursors = this.input.keyboard!.createCursorKeys();

    // Configurar input
    this.setupInput();

    // Deshabilitar menú contextual del click derecho
    this.game.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });

    // Instrucciones iniciales
    this.showInstructions();
  }

  /**
   * Muestra instrucciones cuando no hay imagen cargada
   */
  private showInstructions(): void {
    const { width, height } = this.scale;
    
    const text = this.add.text(width / 2, height / 2, 
      'Carga una imagen usando el panel de controles\n\n' +
      'Click izquierdo/medio: Arrastrar cámara\n' +
      'Click derecho: Seleccionar frame\n' +
      'Rueda del mouse: Zoom\n' +
      'Flechas: Mover cámara', {
      fontSize: '16px',
      fontFamily: GAME_FONT_FAMILY,
      color: '#888888',
      align: 'center',
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    text.setDepth(0);
    text.setName('instructions');
  }

  /**
   * Configura todos los handlers de input
   */
  private setupInput(): void {
    this.input.on('pointerdown', this.handlePointerDown, this);
    this.input.on('pointermove', this.handlePointerMove, this);
    this.input.on('pointerup', this.handlePointerUp, this);
    this.input.on('wheel', this.handleWheel, this);

    // Tecla ESC para deseleccionar
    this.input.keyboard?.on('keydown-ESC', () => {
      this.selectFrame(null);
    });
  }

  /**
   * Maneja el evento pointerdown
   */
  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);

    // Click izquierdo o medio = arrastrar cámara o handles
    if (pointer.leftButtonDown() || pointer.middleButtonDown()) {
      // Verificar si estamos sobre un handle del canvas (redimensionar imagen)
      if (this.loadedImage && this.canvasHandleRects) {
        const canvasHandleHit = this.getCanvasHandleAtPosition(worldPoint.x, worldPoint.y);
        if (canvasHandleHit) {
          this.isDraggingCanvasHandle = true;
          this.canvasHandle = canvasHandleHit;
          this.dragStartX = worldPoint.x;
          this.dragStartY = worldPoint.y;
          return;
        }
      }

      // Verificar si estamos sobre un handle de frame
      if (this.selectedFrameIndex !== null && this.handleRects) {
        const handleHit = this.getHandleAtPosition(worldPoint.x, worldPoint.y);
        if (handleHit) {
          this.isDraggingHandle = true;
          this.dragHandle = handleHit;
          this.dragStartX = worldPoint.x;
          this.dragStartY = worldPoint.y;
          return;
        }
      }

      this.isDraggingCamera = true;
      this.dragStartX = pointer.x;
      this.dragStartY = pointer.y;
      this.dragStartScrollX = this.cameras.main.scrollX;
      this.dragStartScrollY = this.cameras.main.scrollY;
      return;
    }

    // Click derecho = seleccionar frame
    if (pointer.rightButtonDown()) {
      const frameIndex = this.getFrameAtPosition(worldPoint.x, worldPoint.y);
      this.selectFrame(frameIndex);
    }
  }

  /**
   * Maneja el evento pointermove
   */
  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);

    // Arrastrar handle del canvas (redimensionar imagen)
    if (this.isDraggingCanvasHandle && this.canvasHandle) {
      const deltaX = worldPoint.x - this.dragStartX;
      const deltaY = worldPoint.y - this.dragStartY;
      this.adjustCanvasBorder(this.canvasHandle, deltaX, deltaY);
      this.dragStartX = worldPoint.x;
      this.dragStartY = worldPoint.y;
      return;
    }

    // Arrastrar handle de frame
    if (this.isDraggingHandle && this.dragHandle && this.selectedFrameIndex !== null) {
      const deltaX = worldPoint.x - this.dragStartX;
      const deltaY = worldPoint.y - this.dragStartY;
      this.adjustFrameBorder(this.selectedFrameIndex, this.dragHandle, deltaX, deltaY);
      this.dragStartX = worldPoint.x;
      this.dragStartY = worldPoint.y;
      return;
    }

    // Arrastrar cámara
    if (this.isDraggingCamera) {
      const deltaX = this.dragStartX - pointer.x;
      const deltaY = this.dragStartY - pointer.y;
      const cam = this.cameras.main;
      cam.setScroll(
        this.dragStartScrollX + deltaX / cam.zoom,
        this.dragStartScrollY + deltaY / cam.zoom
      );
      return;
    }

    // Hover sobre frames
    if (this.loadedImage) {
      const frameIndex = this.getFrameAtPosition(worldPoint.x, worldPoint.y);
      if (frameIndex !== this.hoveredFrameIndex) {
        this.hoveredFrameIndex = frameIndex;
        this.renderFrameRectangles();
        
        if (frameIndex !== null) {
          this.showFrameTooltip(frameIndex, pointer.x, pointer.y);
        } else {
          this.hideTooltip();
        }
      } else if (frameIndex !== null) {
        this.updateTooltipPosition(pointer.x, pointer.y);
      }
    }

    // Cambiar cursor según el handle sobre el que estamos
    let cursorSet = false;

    // Primero verificar handles del canvas (tienen prioridad)
    if (this.loadedImage && this.canvasHandleRects) {
      const canvasHandleHit = this.getCanvasHandleAtPosition(worldPoint.x, worldPoint.y);
      if (canvasHandleHit) {
        this.game.canvas.style.cursor = this.getCursorForCanvasHandle(canvasHandleHit);
        cursorSet = true;
      }
    }

    // Luego verificar handles de frame
    if (!cursorSet && this.selectedFrameIndex !== null && this.handleRects) {
      const handleHit = this.getHandleAtPosition(worldPoint.x, worldPoint.y);
      if (handleHit === 'left' || handleHit === 'right') {
        this.game.canvas.style.cursor = 'ew-resize';
        cursorSet = true;
      } else if (handleHit === 'top' || handleHit === 'bottom') {
        this.game.canvas.style.cursor = 'ns-resize';
        cursorSet = true;
      }
    }

    if (!cursorSet) {
      this.game.canvas.style.cursor = 'default';
    }
  }

  /**
   * Maneja el evento pointerup
   */
  private handlePointerUp(): void {
    this.isDraggingCamera = false;
    this.isDraggingHandle = false;
    this.isDraggingCanvasHandle = false;
    this.dragHandle = null;
    this.canvasHandle = null;
  }

  /**
   * Maneja el zoom con la rueda del mouse
   */
  private handleWheel(
    pointer: Phaser.Input.Pointer,
    gameObjects: Phaser.GameObjects.GameObject[],
    deltaX: number,
    deltaY: number
  ): void {
    const cam = this.cameras.main;
    const oldZoom = cam.zoom;
    const zoomSpeed = 0.1;
    const minZoom = 0.1;
    const maxZoom = 10.0;

    let newZoom = oldZoom;
    if (deltaY < 0) {
      newZoom = Math.min(maxZoom, oldZoom + zoomSpeed);
    } else if (deltaY > 0) {
      newZoom = Math.max(minZoom, oldZoom - zoomSpeed);
    }

    if (newZoom === oldZoom) return;

    // Zoom centrado en la posición del mouse
    const mouseX = this.input.activePointer.x;
    const mouseY = this.input.activePointer.y;
    const worldPointBefore = cam.getWorldPoint(mouseX, mouseY);

    cam.setZoom(newZoom);

    const worldPointAfter = cam.getWorldPoint(mouseX, mouseY);
    cam.scrollX += worldPointBefore.x - worldPointAfter.x;
    cam.scrollY += worldPointBefore.y - worldPointAfter.y;

    this.renderGrid();
  }

  /**
   * Obtiene el índice del frame en una posición dada
   */
  private getFrameAtPosition(x: number, y: number): number | null {
    if (!this.loadedImage || this.config.frameWidth === 0 || this.config.frameHeight === 0) {
      return null;
    }

    // Calcular el área visible con crop
    const croppedWidth = this.imageWidth - this.canvasCropLeft - this.canvasCropRight;
    const croppedHeight = this.imageHeight - this.canvasCropTop - this.canvasCropBottom;
    const offsetX = (this.canvasCropLeft - this.canvasCropRight) / 2;
    const offsetY = (this.canvasCropTop - this.canvasCropBottom) / 2;

    const imgLeft = offsetX - croppedWidth / 2;
    const imgTop = offsetY - croppedHeight / 2;

    // Verificar si está dentro del área visible
    if (x < imgLeft || x >= imgLeft + croppedWidth ||
        y < imgTop || y >= imgTop + croppedHeight) {
      return null;
    }

    // Calcular posición relativa al área visible (no a la imagen original)
    const relX = x - imgLeft;
    const relY = y - imgTop;
    const col = Math.floor(relX / this.config.frameWidth);
    const row = Math.floor(relY / this.config.frameHeight);

    if (col < 0 || col >= this.config.columns || row < 0 || row >= this.config.rows) {
      return null;
    }

    return row * this.config.columns + col;
  }

  /**
   * Obtiene el handle en una posición dada
   */
  private getHandleAtPosition(x: number, y: number): 'left' | 'right' | 'top' | 'bottom' | null {
    if (!this.handleRects) return null;

    if (this.handleRects.left.contains(x, y)) return 'left';
    if (this.handleRects.right.contains(x, y)) return 'right';
    if (this.handleRects.top.contains(x, y)) return 'top';
    if (this.handleRects.bottom.contains(x, y)) return 'bottom';

    return null;
  }

  /**
   * Obtiene el handle del canvas en una posición dada
   */
  private getCanvasHandleAtPosition(x: number, y: number): CanvasHandleType {
    if (!this.canvasHandleRects) return null;

    // Esquinas primero (tienen prioridad)
    if (this.canvasHandleRects['top-left']?.contains(x, y)) return 'top-left';
    if (this.canvasHandleRects['top-right']?.contains(x, y)) return 'top-right';
    if (this.canvasHandleRects['bottom-left']?.contains(x, y)) return 'bottom-left';
    if (this.canvasHandleRects['bottom-right']?.contains(x, y)) return 'bottom-right';

    // Luego los bordes
    if (this.canvasHandleRects['left']?.contains(x, y)) return 'left';
    if (this.canvasHandleRects['right']?.contains(x, y)) return 'right';
    if (this.canvasHandleRects['top']?.contains(x, y)) return 'top';
    if (this.canvasHandleRects['bottom']?.contains(x, y)) return 'bottom';

    return null;
  }

  /**
   * Obtiene el cursor apropiado para un handle del canvas
   */
  private getCursorForCanvasHandle(handle: CanvasHandleType): string {
    switch (handle) {
      case 'left':
      case 'right':
        return 'ew-resize';
      case 'top':
      case 'bottom':
        return 'ns-resize';
      case 'top-left':
      case 'bottom-right':
        return 'nwse-resize';
      case 'top-right':
      case 'bottom-left':
        return 'nesw-resize';
      default:
        return 'default';
    }
  }

  /**
   * Ajusta los bordes del canvas (recorte de la imagen)
   */
  private adjustCanvasBorder(handle: CanvasHandleType, deltaX: number, deltaY: number): void {
    switch (handle) {
      case 'left':
        this.canvasCropLeft = Math.max(0, this.canvasCropLeft + deltaX);
        break;
      case 'right':
        this.canvasCropRight = Math.max(0, this.canvasCropRight - deltaX);
        break;
      case 'top':
        this.canvasCropTop = Math.max(0, this.canvasCropTop + deltaY);
        break;
      case 'bottom':
        this.canvasCropBottom = Math.max(0, this.canvasCropBottom - deltaY);
        break;
      case 'top-left':
        this.canvasCropLeft = Math.max(0, this.canvasCropLeft + deltaX);
        this.canvasCropTop = Math.max(0, this.canvasCropTop + deltaY);
        break;
      case 'top-right':
        this.canvasCropRight = Math.max(0, this.canvasCropRight - deltaX);
        this.canvasCropTop = Math.max(0, this.canvasCropTop + deltaY);
        break;
      case 'bottom-left':
        this.canvasCropLeft = Math.max(0, this.canvasCropLeft + deltaX);
        this.canvasCropBottom = Math.max(0, this.canvasCropBottom - deltaY);
        break;
      case 'bottom-right':
        this.canvasCropRight = Math.max(0, this.canvasCropRight - deltaX);
        this.canvasCropBottom = Math.max(0, this.canvasCropBottom - deltaY);
        break;
    }

    // Limitar para que no excedan el tamaño de la imagen
    const maxCropX = this.imageWidth - 10;
    const maxCropY = this.imageHeight - 10;
    this.canvasCropLeft = Math.min(this.canvasCropLeft, maxCropX - this.canvasCropRight);
    this.canvasCropRight = Math.min(this.canvasCropRight, maxCropX - this.canvasCropLeft);
    this.canvasCropTop = Math.min(this.canvasCropTop, maxCropY - this.canvasCropBottom);
    this.canvasCropBottom = Math.min(this.canvasCropBottom, maxCropY - this.canvasCropTop);

    // Recalcular dimensiones de frames basándose en el tamaño visible
    this.recalculateFrameDimensions();

    // Re-renderizar
    this.updateImageCrop();
    this.renderCanvasHandles();
    this.renderGrid();
    this.renderFrameRectangles();
    this.renderSelection();
    this.notifyConfigChanged();
    this.notifyCanvasCropChanged();
  }

  /**
   * Recalcula las dimensiones de los frames basándose en el tamaño visible (con crop)
   */
  private recalculateFrameDimensions(): void {
    if (!this.loadedImage || this.config.columns === 0 || this.config.rows === 0) return;

    // Calcular el tamaño visible después del crop
    const croppedWidth = this.imageWidth - this.canvasCropLeft - this.canvasCropRight;
    const croppedHeight = this.imageHeight - this.canvasCropTop - this.canvasCropBottom;

    // Recalcular dimensiones de frames basándose en el tamaño visible
    const oldFrameWidth = this.config.frameWidth;
    const oldFrameHeight = this.config.frameHeight;
    
    this.config.frameWidth = Math.floor(croppedWidth / this.config.columns);
    this.config.frameHeight = Math.floor(croppedHeight / this.config.rows);

    // Ajustar los ajustes de frames existentes para que sean relativos al nuevo tamaño
    // Los ajustes deben escalarse proporcionalmente
    if (oldFrameWidth > 0 && oldFrameHeight > 0) {
      const scaleX = this.config.frameWidth / oldFrameWidth;
      const scaleY = this.config.frameHeight / oldFrameHeight;

      // Ajustar el offset del crop para que sea relativo al nuevo tamaño visible
      const cropOffsetX = this.canvasCropLeft;
      const cropOffsetY = this.canvasCropTop;

      this.config.adjustments.forEach((adjustment) => {
        // Convertir las coordenadas de crop de la imagen original al área visible
        // Primero, calcular la posición original del frame en la imagen completa
        const col = adjustment.frameIndex % this.config.columns;
        const row = Math.floor(adjustment.frameIndex / this.config.columns);
        const originalFrameX = col * oldFrameWidth;
        const originalFrameY = row * oldFrameHeight;

        // Si el frame está completamente fuera del área visible, eliminarlo
        const originalFrameRight = originalFrameX + oldFrameWidth;
        const originalFrameBottom = originalFrameY + oldFrameHeight;
        
        if (originalFrameRight <= cropOffsetX || 
            originalFrameX >= this.imageWidth - this.canvasCropRight ||
            originalFrameBottom <= cropOffsetY ||
            originalFrameY >= this.imageHeight - this.canvasCropBottom) {
          this.config.adjustments.delete(adjustment.frameIndex);
          return;
        }

        // Ajustar cropX y cropY para que sean relativos al área visible
        // Si el frame original empezaba antes del crop, ajustar
        const newCropX = Math.max(0, adjustment.cropX - cropOffsetX);
        const newCropY = Math.max(0, adjustment.cropY - cropOffsetY);

        // Escalar las dimensiones
        adjustment.cropX = newCropX;
        adjustment.cropY = newCropY;
        adjustment.cropWidth = Math.max(1, Math.floor(adjustment.cropWidth * scaleX));
        adjustment.cropHeight = Math.max(1, Math.floor(adjustment.cropHeight * scaleY));
      });
    }
  }

  /**
   * Actualiza el crop de la imagen
   */
  private updateImageCrop(): void {
    if (!this.loadedImage) return;

    // Aplicar el crop usando setCrop de Phaser
    this.loadedImage.setCrop(
      this.canvasCropLeft,
      this.canvasCropTop,
      this.imageWidth - this.canvasCropLeft - this.canvasCropRight,
      this.imageHeight - this.canvasCropTop - this.canvasCropBottom
    );

    // Ajustar la posición de la imagen para mantenerla centrada
    const croppedWidth = this.imageWidth - this.canvasCropLeft - this.canvasCropRight;
    const croppedHeight = this.imageHeight - this.canvasCropTop - this.canvasCropBottom;
    
    // La imagen se posiciona por su centro, pero el crop cambia dónde empieza el contenido visible
    // Necesitamos ajustar la posición para que el área visible quede centrada
    const offsetX = (this.canvasCropLeft - this.canvasCropRight) / 2;
    const offsetY = (this.canvasCropTop - this.canvasCropBottom) / 2;
    this.loadedImage.setPosition(offsetX, offsetY);
  }

  /**
   * Renderiza los handles del canvas
   */
  private renderCanvasHandles(): void {
    if (!this.canvasHandleGraphics || !this.loadedImage) return;
    this.canvasHandleGraphics.clear();

    // Dimensiones del área visible (con crop aplicado)
    const croppedWidth = this.imageWidth - this.canvasCropLeft - this.canvasCropRight;
    const croppedHeight = this.imageHeight - this.canvasCropTop - this.canvasCropBottom;
    
    // Posición del área visible
    const offsetX = (this.canvasCropLeft - this.canvasCropRight) / 2;
    const offsetY = (this.canvasCropTop - this.canvasCropBottom) / 2;
    
    const left = offsetX - croppedWidth / 2;
    const right = offsetX + croppedWidth / 2;
    const top = offsetY - croppedHeight / 2;
    const bottom = offsetY + croppedHeight / 2;
    const centerX = offsetX;
    const centerY = offsetY;

    const handleSize = 10;
    const handleColor = 0x00aaff;
    const borderColor = 0x0088dd;

    // Dibujar borde del canvas
    this.canvasHandleGraphics.lineStyle(2, borderColor, 0.8);
    this.canvasHandleGraphics.strokeRect(left, top, croppedWidth, croppedHeight);

    // Dibujar handles (cuadrados en bordes y esquinas)
    this.canvasHandleGraphics.fillStyle(handleColor, 1);
    this.canvasHandleGraphics.lineStyle(1, 0xffffff, 1);

    // Esquinas
    this.canvasHandleGraphics.fillRect(left - handleSize / 2, top - handleSize / 2, handleSize, handleSize);
    this.canvasHandleGraphics.strokeRect(left - handleSize / 2, top - handleSize / 2, handleSize, handleSize);

    this.canvasHandleGraphics.fillRect(right - handleSize / 2, top - handleSize / 2, handleSize, handleSize);
    this.canvasHandleGraphics.strokeRect(right - handleSize / 2, top - handleSize / 2, handleSize, handleSize);

    this.canvasHandleGraphics.fillRect(left - handleSize / 2, bottom - handleSize / 2, handleSize, handleSize);
    this.canvasHandleGraphics.strokeRect(left - handleSize / 2, bottom - handleSize / 2, handleSize, handleSize);

    this.canvasHandleGraphics.fillRect(right - handleSize / 2, bottom - handleSize / 2, handleSize, handleSize);
    this.canvasHandleGraphics.strokeRect(right - handleSize / 2, bottom - handleSize / 2, handleSize, handleSize);

    // Bordes (centros)
    this.canvasHandleGraphics.fillRect(centerX - handleSize / 2, top - handleSize / 2, handleSize, handleSize);
    this.canvasHandleGraphics.strokeRect(centerX - handleSize / 2, top - handleSize / 2, handleSize, handleSize);

    this.canvasHandleGraphics.fillRect(centerX - handleSize / 2, bottom - handleSize / 2, handleSize, handleSize);
    this.canvasHandleGraphics.strokeRect(centerX - handleSize / 2, bottom - handleSize / 2, handleSize, handleSize);

    this.canvasHandleGraphics.fillRect(left - handleSize / 2, centerY - handleSize / 2, handleSize, handleSize);
    this.canvasHandleGraphics.strokeRect(left - handleSize / 2, centerY - handleSize / 2, handleSize, handleSize);

    this.canvasHandleGraphics.fillRect(right - handleSize / 2, centerY - handleSize / 2, handleSize, handleSize);
    this.canvasHandleGraphics.strokeRect(right - handleSize / 2, centerY - handleSize / 2, handleSize, handleSize);

    // Guardar rectángulos para detección de clicks
    this.canvasHandleRects = {
      'top-left': new Phaser.Geom.Rectangle(left - handleSize, top - handleSize, handleSize * 2, handleSize * 2),
      'top-right': new Phaser.Geom.Rectangle(right - handleSize, top - handleSize, handleSize * 2, handleSize * 2),
      'bottom-left': new Phaser.Geom.Rectangle(left - handleSize, bottom - handleSize, handleSize * 2, handleSize * 2),
      'bottom-right': new Phaser.Geom.Rectangle(right - handleSize, bottom - handleSize, handleSize * 2, handleSize * 2),
      'top': new Phaser.Geom.Rectangle(centerX - handleSize, top - handleSize, handleSize * 2, handleSize * 2),
      'bottom': new Phaser.Geom.Rectangle(centerX - handleSize, bottom - handleSize, handleSize * 2, handleSize * 2),
      'left': new Phaser.Geom.Rectangle(left - handleSize, centerY - handleSize, handleSize * 2, handleSize * 2),
      'right': new Phaser.Geom.Rectangle(right - handleSize, centerY - handleSize, handleSize * 2, handleSize * 2),
    };
  }

  /**
   * Selecciona un frame
   */
  private selectFrame(frameIndex: number | null): void {
    this.selectedFrameIndex = frameIndex;
    this.renderSelection();

    let adjustment: FrameAdjustment | null = null;
    if (frameIndex !== null) {
      adjustment = this.getOrCreateAdjustment(frameIndex);
    }

    if (SpriteFrameEditorScene.onFrameSelected) {
      SpriteFrameEditorScene.onFrameSelected(frameIndex, adjustment);
    }
  }

  /**
   * Obtiene o crea un ajuste para un frame
   */
  private getOrCreateAdjustment(frameIndex: number): FrameAdjustment {
    let adjustment = this.config.adjustments.get(frameIndex);
    if (!adjustment) {
      // Calcular posición del frame original
      const col = frameIndex % this.config.columns;
      const row = Math.floor(frameIndex / this.config.columns);
      const frameX = col * this.config.frameWidth;
      const frameY = row * this.config.frameHeight;

      adjustment = {
        frameIndex,
        cropX: frameX,
        cropY: frameY,
        cropWidth: this.config.frameWidth,
        cropHeight: this.config.frameHeight,
        offsetX: 0,
        offsetY: 0,
      };
      this.config.adjustments.set(frameIndex, adjustment);
    }
    return adjustment;
  }

  /**
   * Ajusta el borde de un frame
   */
  public adjustFrameBorder(
    frameIndex: number,
    edge: 'left' | 'right' | 'top' | 'bottom',
    deltaX: number,
    deltaY: number
  ): void {
    const adjustment = this.getOrCreateAdjustment(frameIndex);

    switch (edge) {
      case 'left':
        adjustment.cropX += deltaX;
        adjustment.cropWidth -= deltaX;
        break;
      case 'right':
        adjustment.cropWidth += deltaX;
        break;
      case 'top':
        adjustment.cropY += deltaY;
        adjustment.cropHeight -= deltaY;
        break;
      case 'bottom':
        adjustment.cropHeight += deltaY;
        break;
    }

    // Clamp valores para evitar negativos
    adjustment.cropWidth = Math.max(1, adjustment.cropWidth);
    adjustment.cropHeight = Math.max(1, adjustment.cropHeight);

    this.config.adjustments.set(frameIndex, adjustment);
    this.renderSelection();
    this.notifyConfigChanged();

    if (SpriteFrameEditorScene.onFrameSelected) {
      SpriteFrameEditorScene.onFrameSelected(frameIndex, adjustment);
    }
  }

  /**
   * Renderiza la grilla de fondo
   */
  private renderGrid(): void {
    if (!this.gridGraphics) return;
    this.gridGraphics.clear();

    if (!this.showGrid || !this.loadedImage) return;

    // Calcular el área visible con crop
    const croppedWidth = this.imageWidth - this.canvasCropLeft - this.canvasCropRight;
    const croppedHeight = this.imageHeight - this.canvasCropTop - this.canvasCropBottom;
    const offsetX = (this.canvasCropLeft - this.canvasCropRight) / 2;
    const offsetY = (this.canvasCropTop - this.canvasCropBottom) / 2;

    const imgLeft = offsetX - croppedWidth / 2;
    const imgTop = offsetY - croppedHeight / 2;

    // Fondo semi-transparente para mejorar la visibilidad de la grilla sobre la imagen
    this.gridGraphics.fillStyle(0x000000, 0.1);
    this.gridGraphics.fillRect(imgLeft, imgTop, croppedWidth, croppedHeight);

    // Grilla menor (cada 10 píxeles) - más visible
    this.gridGraphics.lineStyle(1, 0x555555, 0.6);
    const gridSize = 10;
    for (let x = imgLeft; x <= imgLeft + croppedWidth; x += gridSize) {
      this.gridGraphics.moveTo(x, imgTop);
      this.gridGraphics.lineTo(x, imgTop + croppedHeight);
    }
    for (let y = imgTop; y <= imgTop + croppedHeight; y += gridSize) {
      this.gridGraphics.moveTo(imgLeft, y);
      this.gridGraphics.lineTo(imgLeft + croppedWidth, y);
    }
    this.gridGraphics.strokePath();

    // Grilla mayor (cada 50 píxeles) - más visible para orientación
    this.gridGraphics.lineStyle(1, 0x777777, 0.8);
    const majorGridSize = 50;
    for (let x = imgLeft; x <= imgLeft + croppedWidth; x += majorGridSize) {
      this.gridGraphics.moveTo(x, imgTop);
      this.gridGraphics.lineTo(x, imgTop + croppedHeight);
    }
    for (let y = imgTop; y <= imgTop + croppedHeight; y += majorGridSize) {
      this.gridGraphics.moveTo(imgLeft, y);
      this.gridGraphics.lineTo(imgLeft + croppedWidth, y);
    }
    this.gridGraphics.strokePath();
  }

  /**
   * Renderiza los rectángulos de frames
   */
  private renderFrameRectangles(): void {
    if (!this.frameRectGraphics || !this.loadedImage) return;
    this.frameRectGraphics.clear();

    // Asegurar que los frames estén sobre la imagen
    this.frameRectGraphics.setDepth(2);

    // Calcular el área visible con crop
    const croppedWidth = this.imageWidth - this.canvasCropLeft - this.canvasCropRight;
    const croppedHeight = this.imageHeight - this.canvasCropTop - this.canvasCropBottom;
    const offsetX = (this.canvasCropLeft - this.canvasCropRight) / 2;
    const offsetY = (this.canvasCropTop - this.canvasCropBottom) / 2;

    // Los frames ahora se calculan desde el área visible (0,0 del área visible)
    const imgLeft = offsetX - croppedWidth / 2;
    const imgTop = offsetY - croppedHeight / 2;

    for (let row = 0; row < this.config.rows; row++) {
      for (let col = 0; col < this.config.columns; col++) {
        const frameIndex = row * this.config.columns + col;
        const frameX = col * this.config.frameWidth;
        const frameY = row * this.config.frameHeight;

        // Verificar si el frame está dentro del área visible
        if (frameX >= croppedWidth || frameY >= croppedHeight) {
          continue;
        }

        const x = imgLeft + frameX;
        const y = imgTop + frameY;

        // Color basado en la fila (dirección)
        const color = DIRECTION_COLORS[row] || 0xffffff;
        const isHovered = frameIndex === this.hoveredFrameIndex;
        const isSelected = frameIndex === this.selectedFrameIndex;

        // Dibujar rectángulo del frame con mayor visibilidad
        const lineAlpha = isSelected ? 1.0 : (isHovered ? 0.9 : 0.8);
        const fillAlpha = isHovered ? 0.3 : 0.15;
        
        this.frameRectGraphics.lineStyle(isSelected ? 3 : 2, color, lineAlpha);
        this.frameRectGraphics.strokeRect(x, y, this.config.frameWidth, this.config.frameHeight);

        // Siempre dibujar el fondo semi-transparente para mejor visibilidad
        this.frameRectGraphics.fillStyle(color, fillAlpha);
        this.frameRectGraphics.fillRect(x, y, this.config.frameWidth, this.config.frameHeight);
      }
    }
  }

  /**
   * Renderiza la selección actual y handles
   */
  private renderSelection(): void {
    if (!this.selectionGraphics || !this.handleGraphics) return;
    this.selectionGraphics.clear();
    this.handleGraphics.clear();
    this.handleRects = null;

    if (this.selectedFrameIndex === null || !this.loadedImage) return;

    const adjustment = this.getOrCreateAdjustment(this.selectedFrameIndex);
    
    // Calcular el área visible con crop
    const croppedWidth = this.imageWidth - this.canvasCropLeft - this.canvasCropRight;
    const croppedHeight = this.imageHeight - this.canvasCropTop - this.canvasCropBottom;
    const offsetX = (this.canvasCropLeft - this.canvasCropRight) / 2;
    const offsetY = (this.canvasCropTop - this.canvasCropBottom) / 2;

    // Los frames ahora se calculan desde el área visible (0,0 del área visible)
    const imgLeft = offsetX - croppedWidth / 2;
    const imgTop = offsetY - croppedHeight / 2;

    // Posición del frame seleccionado con ajustes (ajustes son relativos al área visible)
    const left = imgLeft + adjustment.cropX;
    const right = left + adjustment.cropWidth;
    const top = imgTop + adjustment.cropY;
    const bottom = top + adjustment.cropHeight;
    const centerX = (left + right) / 2;
    const centerY = (top + bottom) / 2;

    // Rectángulo de selección
    this.selectionGraphics.lineStyle(2, 0xffff00, 1);
    this.selectionGraphics.strokeRect(
      left,
      top,
      adjustment.cropWidth,
      adjustment.cropHeight
    );

    // Dibujar handles
    const handleSize = 8;
    const handleColor = 0xffffff;

    // Handle izquierdo
    this.handleGraphics.fillStyle(handleColor, 1);
    this.handleGraphics.fillRect(left - handleSize / 2, centerY - handleSize / 2, handleSize, handleSize);

    // Handle derecho
    this.handleGraphics.fillRect(right - handleSize / 2, centerY - handleSize / 2, handleSize, handleSize);

    // Handle superior
    this.handleGraphics.fillRect(centerX - handleSize / 2, top - handleSize / 2, handleSize, handleSize);

    // Handle inferior
    this.handleGraphics.fillRect(centerX - handleSize / 2, bottom - handleSize / 2, handleSize, handleSize);

    // Guardar rectángulos de handles para detección de clicks
    this.handleRects = {
      left: new Phaser.Geom.Rectangle(left - handleSize, centerY - handleSize, handleSize * 2, handleSize * 2),
      right: new Phaser.Geom.Rectangle(right - handleSize, centerY - handleSize, handleSize * 2, handleSize * 2),
      top: new Phaser.Geom.Rectangle(centerX - handleSize, top - handleSize, handleSize * 2, handleSize * 2),
      bottom: new Phaser.Geom.Rectangle(centerX - handleSize, bottom - handleSize, handleSize * 2, handleSize * 2),
    };
  }

  /**
   * Muestra tooltip con información del frame
   */
  private showFrameTooltip(frameIndex: number, screenX: number, screenY: number): void {
    this.hideTooltip();

    const row = Math.floor(frameIndex / this.config.columns);
    const col = frameIndex % this.config.columns;
    const directionNames = ['Down', 'Up', 'Left', 'Right'];
    const direction = directionNames[row] || `Row ${row}`;

    const bg = this.add.rectangle(0, 0, 120, 40, 0x000000, 0.9);
    bg.setOrigin(0, 0);
    bg.setScrollFactor(0);

    const text = this.add.text(6, 6, `Frame ${frameIndex}\n${direction} - Col ${col}`, {
      fontSize: '10px',
      fontFamily: GAME_FONT_FAMILY,
      color: '#ffffff',
    });
    text.setScrollFactor(0);

    bg.setSize(text.width + 12, text.height + 12);

    this.tooltipContainer = this.add.container(screenX + 15, screenY + 15);
    this.tooltipContainer.setDepth(100);
    this.tooltipContainer.setScrollFactor(0);
    this.tooltipContainer.add([bg, text]);
  }

  /**
   * Actualiza la posición del tooltip
   */
  private updateTooltipPosition(screenX: number, screenY: number): void {
    if (this.tooltipContainer) {
      this.tooltipContainer.setPosition(screenX + 15, screenY + 15);
    }
  }

  /**
   * Oculta el tooltip
   */
  private hideTooltip(): void {
    if (this.tooltipContainer) {
      this.tooltipContainer.destroy();
      this.tooltipContainer = null;
    }
  }

  /**
   * Notifica cambios de configuración
   */
  private notifyConfigChanged(): void {
    if (SpriteFrameEditorScene.onConfigChanged) {
      SpriteFrameEditorScene.onConfigChanged(this.config);
    }
  }

  /**
   * Notifica cambios del crop del canvas
   */
  private notifyCanvasCropChanged(): void {
    if (SpriteFrameEditorScene.onCanvasCropChanged) {
      SpriteFrameEditorScene.onCanvasCropChanged(this.getCanvasCrop());
    }
  }

  /**
   * Obtiene la información del crop del canvas
   */
  public getCanvasCrop(): CanvasCropInfo {
    return {
      left: this.canvasCropLeft,
      right: this.canvasCropRight,
      top: this.canvasCropTop,
      bottom: this.canvasCropBottom,
      croppedWidth: this.imageWidth - this.canvasCropLeft - this.canvasCropRight,
      croppedHeight: this.imageHeight - this.canvasCropTop - this.canvasCropBottom,
    };
  }

  update(time: number, delta: number): void {
    // Actualizar FPS en el elemento HTML
    const fpsElement = document.getElementById('sprite-frame-editor-fps');
    if (fpsElement) {
      const fps = Math.round(1000 / delta);
      fpsElement.textContent = `${fps}`;
    }

    // Movimiento de cámara con flechas del teclado
    if (this.cursors) {
      const cam = this.cameras.main;
      const speed = 200 * (delta / 1000);

      if (this.cursors.left?.isDown) cam.scrollX -= speed;
      if (this.cursors.right?.isDown) cam.scrollX += speed;
      if (this.cursors.up?.isDown) cam.scrollY -= speed;
      if (this.cursors.down?.isDown) cam.scrollY += speed;
    }
  }

  // ==========================================================================
  // MÉTODOS PÚBLICOS - API para el panel HTML
  // ==========================================================================

  /**
   * Carga una imagen desde un Data URL
   */
  public loadImageFromDataUrl(dataUrl: string, fileName: string): void {
    console.log('[SpriteFrameEditorScene] Loading image:', fileName);

    // Limpiar imagen anterior
    if (this.loadedImage) {
      this.loadedImage.destroy();
      this.loadedImage = null;
    }
    if (this.loadedImageKey) {
      this.textures.remove(this.loadedImageKey);
    }

    // Ocultar instrucciones
    const instructions = this.children.getByName('instructions');
    if (instructions) {
      instructions.destroy();
    }

    // Generar key único
    this.loadedImageKey = `loaded_image_${Date.now()}`;
    this.fileName = fileName;

    // Cargar imagen
    const loader = this.load.image(this.loadedImageKey, dataUrl);
    loader.once('complete', () => {
      const texture = this.textures.get(this.loadedImageKey!);
      if (!texture || texture.key === '__MISSING') {
        console.error('[SpriteFrameEditorScene] Failed to load image');
        return;
      }

      const source = texture.getSourceImage();
      this.imageWidth = source.width;
      this.imageHeight = source.height;

      // Crear imagen centrada en (0, 0)
      this.loadedImage = this.add.image(0, 0, this.loadedImageKey!);
      this.loadedImage.setDepth(0);

      // Calcular dimensiones de frame
      this.config.frameWidth = Math.floor(this.imageWidth / this.config.columns);
      this.config.frameHeight = Math.floor(this.imageHeight / this.config.rows);

      // Limpiar ajustes anteriores
      this.config.adjustments.clear();

      // Resetear crop del canvas
      this.canvasCropLeft = 0;
      this.canvasCropRight = 0;
      this.canvasCropTop = 0;
      this.canvasCropBottom = 0;

      // Centrar cámara
      this.cameras.main.centerOn(0, 0);
      this.cameras.main.setZoom(1);

      // Renderizar
      this.renderGrid();
      this.renderFrameRectangles();
      this.renderCanvasHandles();

      // Notificar
      if (SpriteFrameEditorScene.onImageLoaded) {
        SpriteFrameEditorScene.onImageLoaded(this.imageWidth, this.imageHeight, fileName);
      }
      this.notifyConfigChanged();

      console.log(`[SpriteFrameEditorScene] Image loaded: ${this.imageWidth}x${this.imageHeight}, frame: ${this.config.frameWidth}x${this.config.frameHeight}`);
    });

    this.load.start();
  }

  /**
   * Configura el grid (filas x columnas)
   */
  public setGrid(rows: number, columns: number): void {
    this.config.rows = rows;
    this.config.columns = columns;

    if (this.loadedImage) {
      // Calcular dimensiones de frames basándose en el tamaño visible (con crop)
      const croppedWidth = this.imageWidth - this.canvasCropLeft - this.canvasCropRight;
      const croppedHeight = this.imageHeight - this.canvasCropTop - this.canvasCropBottom;
      
      this.config.frameWidth = Math.floor(croppedWidth / columns);
      this.config.frameHeight = Math.floor(croppedHeight / rows);

      // Limpiar ajustes (ya no son válidos con el nuevo grid)
      this.config.adjustments.clear();
      this.selectedFrameIndex = null;

      this.renderFrameRectangles();
      this.renderSelection();
      this.notifyConfigChanged();
    }
  }

  /**
   * Establece el offset de un frame
   */
  public setFrameOffset(frameIndex: number, offsetX: number, offsetY: number): void {
    const adjustment = this.getOrCreateAdjustment(frameIndex);
    adjustment.offsetX = offsetX;
    adjustment.offsetY = offsetY;
    this.config.adjustments.set(frameIndex, adjustment);
    this.notifyConfigChanged();
  }

  /**
   * Resetea todos los ajustes
   */
  public resetAllAdjustments(): void {
    this.config.adjustments.clear();
    this.selectedFrameIndex = null;

    // Resetear crop del canvas
    this.canvasCropLeft = 0;
    this.canvasCropRight = 0;
    this.canvasCropTop = 0;
    this.canvasCropBottom = 0;

    // Resetear crop de la imagen
    if (this.loadedImage) {
      this.loadedImage.setCrop(0, 0, this.imageWidth, this.imageHeight);
      this.loadedImage.setPosition(0, 0);
    }

    // Recalcular dimensiones de frames basándose en el tamaño original
    if (this.loadedImage && this.config.columns > 0 && this.config.rows > 0) {
      this.config.frameWidth = Math.floor(this.imageWidth / this.config.columns);
      this.config.frameHeight = Math.floor(this.imageHeight / this.config.rows);
    }

    this.renderFrameRectangles();
    this.renderSelection();
    this.renderCanvasHandles();
    this.renderGrid();
    this.notifyConfigChanged();
    this.notifyCanvasCropChanged();

    if (SpriteFrameEditorScene.onFrameSelected) {
      SpriteFrameEditorScene.onFrameSelected(null, null);
    }
  }

  /**
   * Centra la vista en la imagen
   */
  public centerView(): void {
    this.cameras.main.centerOn(0, 0);
    this.cameras.main.setZoom(1);
  }

  /**
   * Alterna la visualización de la grilla
   */
  public toggleGrid(show?: boolean): void {
    this.showGrid = show ?? !this.showGrid;
    this.renderGrid();
  }

  /**
   * Obtiene la configuración actual
   */
  public getConfig(): SpriteSheetConfig {
    return this.config;
  }

  /**
   * Establece el crop del canvas manualmente
   */
  public setCanvasCrop(left: number, right: number, top: number, bottom: number): void {
    this.canvasCropLeft = Math.max(0, left);
    this.canvasCropRight = Math.max(0, right);
    this.canvasCropTop = Math.max(0, top);
    this.canvasCropBottom = Math.max(0, bottom);

    // Limitar para que no excedan el tamaño de la imagen
    const maxCropX = this.imageWidth - 10;
    const maxCropY = this.imageHeight - 10;
    this.canvasCropLeft = Math.min(this.canvasCropLeft, maxCropX - this.canvasCropRight);
    this.canvasCropRight = Math.min(this.canvasCropRight, maxCropX - this.canvasCropLeft);
    this.canvasCropTop = Math.min(this.canvasCropTop, maxCropY - this.canvasCropBottom);
    this.canvasCropBottom = Math.min(this.canvasCropBottom, maxCropY - this.canvasCropTop);

    // Recalcular dimensiones de frames basándose en el tamaño visible
    this.recalculateFrameDimensions();

    this.updateImageCrop();
    this.renderCanvasHandles();
    this.renderGrid();
    this.renderFrameRectangles();
    this.renderSelection();
    this.notifyConfigChanged();
    this.notifyCanvasCropChanged();
  }

  /**
   * Obtiene la imagen original como HTMLImageElement
   */
  public getOriginalImage(): HTMLImageElement | null {
    if (!this.loadedImageKey) return null;
    const texture = this.textures.get(this.loadedImageKey);
    if (!texture || texture.key === '__MISSING') return null;
    return texture.getSourceImage() as HTMLImageElement;
  }

  /**
   * Exporta la imagen ajustada como PNG
   */
  public exportAdjustedPNG(targetFileName?: string): void {
    const originalImage = this.getOriginalImage();
    if (!originalImage) {
      console.warn('[SpriteFrameEditorScene] No image loaded to export');
      return;
    }

    // Calcular el tamaño del canvas de salida (con crop aplicado)
    const outputWidth = this.imageWidth - this.canvasCropLeft - this.canvasCropRight;
    const outputHeight = this.imageHeight - this.canvasCropTop - this.canvasCropBottom;

    // Crear canvas con el tamaño recortado
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('[SpriteFrameEditorScene] Failed to get canvas context');
      return;
    }

    // Dibujar la imagen original con el crop aplicado
    ctx.drawImage(
      originalImage,
      this.canvasCropLeft, this.canvasCropTop,
      outputWidth, outputHeight,
      0, 0,
      outputWidth, outputHeight
    );

    // Descargar
    const link = document.createElement('a');
    const outputName = targetFileName || this.fileName.replace(/\.[^.]+$/, '_adjusted.png');
    link.download = outputName;
    link.href = canvas.toDataURL('image/png');
    link.click();

    console.log(`[SpriteFrameEditorScene] Exported: ${outputName} (${outputWidth}x${outputHeight})`);
  }
}
